import React, { FC, useEffect, useState } from "react";
import validator from "validator";
import {
  Alert,
  Button,
  Input,
  Label,
  Flow,
  FormMessage,
  PhoneNumber,
  theme,
} from "@xometry/xometry_loft";
import fetch from "cross-fetch";
import {
  createAccountButton,
  flowButton,
  Title,
  Content,
} from "./CustomerRFQRegister.style";
import { RegistrationAgreement } from "./CustomerRFQRegisterAgreement";
import { useConfig } from "../../proposals/contexts/configContext";

export type CreateAnonymousUserModalArgs = {
  isOpen: boolean;
  onClose: () => void;
  switchToLoginForm: (userEmail: string) => void;
  onSuccess: (s: string) => void;
};

export const UserRegistrationForm: FC<CreateAnonymousUserModalArgs> = ({
  isOpen,
  onClose,
  switchToLoginForm,
  onSuccess,
}) => {
  const { service } = useConfig();
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [userId, setUserId] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const modalTitle =
    "In order to keep your uploaded files secure and access your quote again, please enter your email address:";

  const {
    origins: { www, auth },
  } = useConfig();

  const onCloseWrapper = () => {
    setError("");
    setEmailError("");
    setPhoneError("");
    onClose();
  };

  const handleLogin = () => {
    onCloseWrapper();
    switchToLoginForm(emailAddress);
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    setPhoneError("");
  };

  const handleEmailAddressChange = (event: { target: { value: string } }) => {
    setEmailAddress(event.target.value);
    setEmailError("");
    setError("");
  };

  const handleInputValidation = () => {
    let isValid = true;
    // using https://www.npmjs.com/package/validator for validation
    // Validate email address
    if (!validator.isEmail(emailAddress)) {
      setEmailError("Enter a Valid Email Address");
      isValid = false;
    }
    // Validate phone number if provided
    if (phoneNumber !== "" && !validator.isMobilePhone(phoneNumber)) {
      setPhoneError("Enter a Valid Phone Number Or Leave The Field Blank");
      isValid = false;
    }
    return isValid;
  };

  // Handles creating anon user account. Returns true if successful.
  const handleUserResponse = async (userResponse: Response) => {
    let responseSuccessful = false;
    const { user_id: resUserId, msg } = await userResponse.json();
    setUserId(resUserId);
    setError(msg);

    if (userResponse.status === 409) {
      // User already has a fully registered account and should log in
      handleLogin();
    } else if (userResponse.status === 422) {
      // User has a partial account and should finish registration
      window.location.assign(
        `${www}/account/forgot-password?email_address=${encodeURIComponent(
          emailAddress
        )}`
      );
    } else if (resUserId) {
      analytics.track("Created new Anonymous User", {
        service,
        context: "Form Stepper",
        emailAddress,
      });
      responseSuccessful = true;
    }
    return responseSuccessful;
  };

  const handleSubmitCreateAccount = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    setIsLoading(true);
    e.preventDefault();
    const isValidInput = handleInputValidation();
    if (!isValidInput) {
      setIsLoading(false);
      return;
    }

    analytics.track("CTA Clicked", {
      service,
      context: "Form Stepper",
      cta_text: "Create Your Account",
      emailAddress,
    });

    let userRegistered = false;
    if (!userId) {
      try {
        const url = `${auth}/v1/user/register-no-password`;
        const postData = {
          email_address: emailAddress,
          phone_number: phoneNumber,
        };

        const userResponse = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(postData),
        });

        userRegistered = await handleUserResponse(userResponse);
      } catch (e) {
        setIsLoading(false);
        console.error(e);
        setError("There was an error creating a user, please try again.");
      }
    }
    // Create parts if user response was successful
    if (userRegistered) {
      onSuccess(emailAddress);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      onCloseWrapper();
    }
  }, [isOpen]);

  return (
    <>
      <Content>
        <Title title={modalTitle}>{modalTitle}</Title>
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          data-testid="emailInput"
          type="text"
          value={emailAddress}
          onChange={handleEmailAddressChange}
          placeholder=""
          invalid={!!emailError}
          autoFocus
        />
        {emailError && (
          <FormMessage style={{ textAlign: "left" }} invalid>
            {emailError}
          </FormMessage>
        )}
        <Label htmlFor="phone">
          Phone Number
          <span> (Optional)</span>
        </Label>
        <PhoneNumber
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          placeholder=""
          invalid={!!phoneError}
        />
        {phoneError && (
          <FormMessage style={{ textAlign: "left" }} invalid>
            {phoneError}
          </FormMessage>
        )}
        {error && (
          <Alert style={{ marginTop: "8px" }} kind={theme.kind.danger}>
            {error}
          </Alert>
        )}
        <RegistrationAgreement WWW_ORIGIN={www} />
        <Button
          kind="primary"
          css={createAccountButton}
          disabled={isLoading}
          onClick={handleSubmitCreateAccount}
          scale="large"
        >
          {isLoading ? (
            <Flow width="60px" kind="disabled" css={flowButton} />
          ) : (
            "Create Your Account"
          )}
        </Button>
      </Content>
      <Content>
        <span>
          Already have an account?
          <Button kind="text-blue" scale="small" onClick={handleLogin}>
            Log In
          </Button>
        </span>
      </Content>
    </>
  );
};
